import React, { useEffect, useMemo, useState } from 'react';
import { ExternalLink, Search, Folder } from 'lucide-react';
import { projects as mockProjects } from '../mock';
import PageHeader from './PageHeader';
import Reveal from './Reveal';

export default function ProyectosPage() {
  const [projects, setProjects] = useState(mockProjects);
  const [category, setCategory] = useState('Todos');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const normalizeProject = (project, fallback = {}) => ({
      ...fallback,
      ...project,
      title: project.title || project.name || fallback.title || '',
      description:
        project.description ||
        project.short_description ||
        fallback.description ||
        '',
      image:
        project.imageFile ||
        project.image ||
        project.image_url ||
        fallback.image ||
         '',
      appUrl:
        project.appUrl ||
        project.website ||
        project.link ||
        project.url ||
        fallback.appUrl ||
        '',
      tags:
        Array.isArray(project.tags) && project.tags.length > 0
          ? project.tags
          : fallback.tags || [],
    });

    fetch('/api/content/projects')
      .then((response) => {
        if (!response.ok) {
          throw new Error('No se pudieron cargar los proyectos');
        }
        return response.json();
      })
      .then((apiProjects) => {
        const legacyProjects = mockProjects.map((project) =>
          normalizeProject(project)
        );

        const normalizedApiProjects = apiProjects
          .filter((project) => project.published !== false)
          .map((project) => {
            const title = (
              project.title ||
              project.name ||
              ''
            ).toLowerCase();

            const fallback = legacyProjects.find(
              (oldProject) =>
                oldProject.title.toLowerCase() === title
            );

            return normalizeProject(project, fallback);
          });

        const apiTitles = new Set(
          normalizedApiProjects.map((project) =>
            project.title.toLowerCase()
          )
        );

        const projectsOnlyInMock = legacyProjects.filter(
          (project) =>
            !apiTitles.has(project.title.toLowerCase())
        );

        setProjects([
          ...projectsOnlyInMock,
          ...normalizedApiProjects,
        ]);
      })
      .catch((requestError) => {
        console.error('Error cargando proyectos:', requestError);
        setError('No se pudieron cargar los proyectos.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const projectCategories = useMemo(() => {
    const categories = projects
      .map((project) => project.category)
      .filter(Boolean);

    return ['Todos', ...new Set(categories)];
  }, [projects]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return projects.filter((project) => {
      const matchCategory =
        category === 'Todos' ||
        project.category === category;

      const matchQuery =
        !normalizedQuery ||
        project.title.toLowerCase().includes(normalizedQuery) ||
        project.description.toLowerCase().includes(normalizedQuery) ||
        project.tags.some((tag) =>
          tag.toLowerCase().includes(normalizedQuery)
        );

      return matchCategory && matchQuery;
    });
  }, [projects, category, query]);

  return (
    <>
      <PageHeader
        tag="Proyectos"
        title="Apps y proyectos publicados por Cibermedida"
        subtitle="Catálogo de aplicaciones y soluciones desplegadas por nuestro equipo en entornos reales."
      />

      <section className="bg-slate-900 py-16">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-10">
            <div className="flex flex-wrap gap-2">
              {projectCategories.map((projectCategory) => (
                <button
                  key={projectCategory}
                  onClick={() => setCategory(projectCategory)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all border ${
                    category === projectCategory
                      ? 'bg-cyan-500 text-slate-900 border-cyan-500'
                      : 'bg-slate-800/60 text-slate-300 border-slate-700 hover:border-cyan-500/60 hover:text-cyan-300'
                  }`}
                >
                  {projectCategory}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-80">
              <Search
                size={18}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar proyecto o tecnología..."
                className="w-full pl-10 pr-4 py-2.5 rounded-md bg-slate-800 border border-slate-700 text-white placeholder:text-slate-500 outline-none focus:border-cyan-500"
              />
            </div>
          </div>

          {loading && (
            <div className="text-center py-20 text-slate-400">
              Cargando proyectos...
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-20 text-red-400">
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-20">
              <Folder
                size={48}
                className="mx-auto text-slate-600 mb-4"
              />
              <p className="text-slate-400">
                No se encontraron proyectos con esos criterios.
              </p>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((project, index) => (
                <Reveal
                  key={project.id || project.title}
                  delay={index * 80}
                  as="article"
                  className="group flex flex-col bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-cyan-500/60 hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-800">
                    {project.image ? (
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Folder
                          size={54}
                          className="text-slate-600"
                        />
                      </div>
                    )}

                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-cyan-500 text-slate-900 text-xs font-bold">
                      {project.category}
                    </span>
                  </div>

                  <div className="p-6 flex flex-col flex-1 gap-4">
                    <h3 className="text-white text-xl font-bold group-hover:text-cyan-400 transition-colors">
                      {project.title}
                    </h3>

                    <p className="text-slate-400 text-sm leading-relaxed line-clamp-3 flex-1">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-1 rounded bg-cyan-500/10 border border-cyan-500/30 text-cyan-300 text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {project.appUrl && (
                      <div className="pt-2 border-t border-slate-700">
                        <a
                          href={project.appUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-semibold hover:shadow-lg hover:shadow-purple-500/30 transition-all"
                        >
                          <ExternalLink size={15} />
                          Ver app
                        </a>
                      </div>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}

          <p className="text-center text-slate-500 text-sm mt-10">
            Mostrando {filtered.length} de {projects.length} proyectos
          </p>
        </div>
      </section>
    </>
  );
}
